import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { Alert, Button, Col, Container, Row, Spinner } from "reactstrap";
import AdminTablePagination from "../../../Components/Common/AdminTablePagination";
import {
  AdminUserDetailResponse,
  AdminUserListItem,
  activateAdminUser,
  blockAdminUser,
  getAdminUser,
  getAdminUserBets,
  updateAdminUserRole,
} from "../../../helpers/api/admin/user";
import { useAuthStore } from "../../../store/useAuthStore";
import type { PaginationMeta, UserBetHistoryItem } from "../../../types/api.generated";
import { isVerified, shortenWallet } from "../User";

type UserDetailState = {
  listUser?: AdminUserListItem;
};

type ActionLoading = "role" | "status" | null;

const pageSize = 20;

const emptyMeta: PaginationMeta = {
  totalItems: 0,
  itemCount: 0,
  itemsPerPage: pageSize,
  totalPages: 1,
  currentPage: 1,
};

export const formatNumber = (value?: number | null) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "-";
  return value.toLocaleString("en-US");
};

export const formatSignedNumber = (value?: number | null) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "-";
  return `${value > 0 ? "+" : ""}${formatNumber(value)}`;
};

export const formatDate = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

export const getDisplayName = (user?: AdminUserDetailResponse["user"] | null) => user?.username || shortenWallet(user?.walletAddress);

export const getInitial = (user?: AdminUserDetailResponse["user"] | null) => {
  const label = getDisplayName(user);
  return label && label !== "-" ? label.charAt(0).toUpperCase() : "?";
};

export const getRoleActionLabel = (role?: string | null) => (role === "admin" ? "Grant user" : "Grant admin");

export const getStatusActionLabel = (enabled?: boolean | null) => (enabled ? "disable user" : "activate user");

export const getBattleParticipantNames = (bet: UserBetHistoryItem) => bet.selectedCharacter.name;

export const getStatusClassName = (status: string) => {
  const normalized = status.toLowerCase();
  if (normalized === "won") return "admin-user-bet-status admin-user-bet-status--won";
  if (normalized === "paid") return "admin-user-bet-status admin-user-bet-status--paid";
  if (normalized === "lost") return "admin-user-bet-status admin-user-bet-status--lost";
  if (normalized === "refunded") return "admin-user-bet-status admin-user-bet-status--refunded";
  return "admin-user-bet-status";
};

const toDetailResponse = (user: AdminUserListItem): AdminUserDetailResponse => ({
  user: {
    id: user.id,
    walletAddress: user.walletAddress,
    pointsBalance: user.pointsBalance,
    enable: user.enable,
    createdAt: user.createdAt,
    updatedAt: user.updatedAt,
    username: user.username,
    email: user.email,
    verify: user.verify,
    emailVerificationExpiresAt: user.emailVerificationExpiresAt,
    role: user.role,
  },
});

const sortBetsByMostRecent = (bets: UserBetHistoryItem[]) => bets.slice().sort((left, right) => {
  const leftTime = new Date(left.createdAt).getTime();
  const rightTime = new Date(right.createdAt).getTime();
  return (Number.isNaN(rightTime) ? 0 : rightTime) - (Number.isNaN(leftTime) ? 0 : leftTime);
});

const UserDetail = () => {
  document.title = "Admin Colosseum | User profile";

  const { userId = "" } = useParams();
  const location = useLocation();
  const { isAdmin, user: currentUser } = useAuthStore();
  const routeUser = (location.state as UserDetailState | null)?.listUser;
  const [detail, setDetail] = useState<AdminUserDetailResponse | null>(routeUser ? toDetailResponse(routeUser) : null);
  const [bets, setBets] = useState<UserBetHistoryItem[]>([]);
  const [meta, setMeta] = useState<PaginationMeta>(emptyMeta);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(!routeUser);
  const [betsLoading, setBetsLoading] = useState<boolean>(true);
  const [actionLoading, setActionLoading] = useState<ActionLoading>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const user = detail?.user;
  const isSelf = !!currentUser?.id && String(currentUser.id) === String(user?.id);
  const sortedBets = useMemo(() => sortBetsByMostRecent(bets), [bets]);
  const visibleNetResult = useMemo(() => bets.reduce((total, bet) => total + Number(bet.netProfit || 0), 0), [bets]);
  const betsPlaced = routeUser?.totalBets ?? meta.totalItems;
  const wonBets = routeUser?.wonBets ?? bets.filter((bet) => ["won", "paid"].includes(bet.status)).length;
  const lostBets = routeUser?.lostBets ?? bets.filter((bet) => bet.status === "lost").length;
  const netResult = routeUser?.netResult ?? visibleNetResult;

  const loadUser = async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const nextDetail = await getAdminUser(userId);
      setDetail(nextDetail);
    } catch (e: any) {
      setError(typeof e === "string" ? e : e?.message || "Failed to load user");
      setDetail(null);
    } finally {
      setLoading(false);
    }
  };

  const loadBets = async (page = currentPage) => {
    if (!userId) return;
    setBetsLoading(true);
    setError(null);
    try {
      const response = await getAdminUserBets(userId, { page, limit: pageSize });
      setBets(response.items);
      setMeta(response.meta);
      setCurrentPage(response.meta.currentPage || page);
    } catch (e: any) {
      setError(typeof e === "string" ? e : e?.message || "Failed to load user bets");
      setBets([]);
      setMeta(emptyMeta);
    } finally {
      setBetsLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    loadBets(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleRoleToggle = async () => {
    if (!user?.id) return;
    const nextRole = user.role === "admin" ? "user" : "admin";
    setActionLoading("role");
    setError(null);
    setSuccess(null);
    try {
      const updated = await updateAdminUserRole(user.id, nextRole);
      setDetail(updated);
      setSuccess("User role updated.");
    } catch (e: any) {
      setError(typeof e === "string" ? e : e?.message || "Failed to update user role");
    } finally {
      setActionLoading(null);
    }
  };

  const handleStatusToggle = async () => {
    if (!user?.id) return;
    setActionLoading("status");
    setError(null);
    setSuccess(null);
    try {
      const updated = user.enable ? await blockAdminUser(user.id) : await activateAdminUser(user.id);
      setDetail(updated);
      setSuccess(user.enable ? "User disabled." : "User activated.");
    } catch (e: any) {
      setError(typeof e === "string" ? e : e?.message || "Failed to update user status");
    } finally {
      setActionLoading(null);
    }
  };

  if (!isAdmin()) {
    return <Navigate to={{ pathname: "/dashboard" }} />;
  }

  return (
    <div className="page-content admin-user-detail-page" data-cy="admin-user-detail-page">
      <Container fluid>
        <Link to="/admin/users" className="admin-users-back">← Back to users</Link>

        {loading ? (
          <div className="text-center py-5">
            <Spinner size="sm" className="me-2" /> Loading user...
          </div>
        ) : (
          <>
            <div className="admin-users-heading">
              <h4 className="mb-1">
                <span className="gradient-text-brand">{getDisplayName(user)}</span>
              </h4>
              <p className="text-muted mb-0">User profile, permissions and full betting history.</p>
            </div>

            {error && <Alert color="danger" role="alert">{error}</Alert>}
            {success && <Alert color="success" role="status">{success}</Alert>}

            {user ? (
              <Row className="g-3 align-items-start">
                <Col lg={4} xl={3}>
                  <aside className="admin-user-card">
                    <span className="admin-user-card__avatar">{getInitial(user)}</span>
                    <h5>{getDisplayName(user)}</h5>
                    <button
                      type="button"
                      className="admin-user-wallet"
                      onClick={() => navigator.clipboard?.writeText(user.walletAddress)}
                      aria-label="Copy wallet address"
                    >
                      {shortenWallet(user.walletAddress)} <i className="ri-file-copy-line" aria-hidden="true" />
                    </button>
                    <dl>
                      <div>
                        <dt>Email</dt>
                        <dd>{user.email || "-"}</dd>
                      </div>
                      <div>
                        <dt>Verified</dt>
                        <dd>
                          <span className={isVerified(user.verify) ? "admin-users-badge admin-users-badge--yes" : "admin-users-badge admin-users-badge--no"}>
                            {isVerified(user.verify) ? "Yes" : "No"}
                          </span>
                        </dd>
                      </div>
                      <div>
                        <dt>Joined</dt>
                        <dd>{formatDate(user.createdAt)}</dd>
                      </div>
                      <div>
                        <dt>Last update</dt>
                        <dd>{formatDate(user.updatedAt)}</dd>
                      </div>
                      <div>
                        <dt>Role</dt>
                        <dd>{user.role === "admin" ? "Admin" : "Player"}</dd>
                      </div>
                      <div>
                        <dt>Status</dt>
                        <dd className={user.enable ? "admin-users-value--positive" : "admin-users-value--negative"}>
                          {user.enable ? "Active" : "Inactive"}
                        </dd>
                      </div>
                    </dl>

                    <div className="admin-user-card__actions">
                      <Button
                        color="primary"
                        disabled={isSelf || actionLoading === "role"}
                        onClick={handleRoleToggle}
                        data-cy="admin-user-toggle-role"
                      >
                        {actionLoading === "role" ? <><Spinner size="sm" className="me-2" /> Updating...</> : getRoleActionLabel(user.role)}
                      </Button>
                      <Button
                        color={user.enable ? "danger" : "success"}
                        disabled={isSelf || actionLoading === "status"}
                        onClick={handleStatusToggle}
                        data-cy="admin-user-toggle-status"
                      >
                        {actionLoading === "status" ? <><Spinner size="sm" className="me-2" /> Updating...</> : getStatusActionLabel(user.enable)}
                      </Button>
                    </div>
                    {isSelf && <p className="text-muted mt-3 mb-0">You cannot change your own role or access.</p>}
                  </aside>
                </Col>

                <Col lg={8} xl={9}>
                  <Row className="g-3 admin-users-summary">
                    <Col md={4}>
                      <div className="admin-users-stat">
                        <span>Balance</span>
                        <strong>{formatNumber(user.pointsBalance)} <small>PTS</small></strong>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="admin-users-stat">
                        <span>Bets placed</span>
                        <strong>{formatNumber(betsPlaced)} <small>{formatNumber(wonBets)}W-{formatNumber(lostBets)}L</small></strong>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="admin-users-stat">
                        <span>Net result</span>
                        <strong className={netResult >= 0 ? "admin-users-value--positive" : "admin-users-value--negative"}>
                          {formatSignedNumber(netResult)}
                        </strong>
                      </div>
                    </Col>
                  </Row>

                  <section className="admin-users-panel admin-user-bets-panel">
                    <header className="admin-user-bets-panel__header">
                      <h5>Bet history · {formatNumber(meta.totalItems)} bets</h5>
                    </header>

                    <div className="table-responsive admin-user-bets-table-wrap">
                      <table className="table align-middle admin-user-bets-table">
                        <thead>
                          <tr>
                            <th>Battle</th>
                            <th>Bet on</th>
                            <th>Stake</th>
                            <th>Payout</th>
                            <th>Status</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {betsLoading ? (
                            <tr>
                              <td colSpan={6} className="text-center py-5">
                                <Spinner size="sm" className="me-2" /> Loading bets...
                              </td>
                            </tr>
                          ) : sortedBets.length > 0 ? (
                            sortedBets.map((bet) => {
                              const net = Number(bet.netProfit || 0);
                              return (
                                <tr key={bet.id} data-cy="admin-user-bet-row">
                                  <td>{bet.battleTitle || bet.battleId}</td>
                                  <td>
                                    <span className="admin-user-bet-side">• {getBattleParticipantNames(bet) || "-"}</span>
                                  </td>
                                  <td><strong>{formatNumber(bet.stakedPoints)}</strong> <span className="text-muted">PTS</span></td>
                                  <td className={net > 0 ? "admin-users-value--positive" : undefined}>
                                    {net > 0 ? formatSignedNumber(net) : "-"}
                                  </td>
                                  <td><span className={getStatusClassName(bet.status)}>{bet.status}</span></td>
                                  <td>{formatDate(bet.createdAt)}</td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={6} className="text-center text-muted py-5">No bets found</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <footer className="admin-user-bets-footer">
                      <span>
                        Showing {meta.totalItems === 0 ? 0 : ((meta.currentPage - 1) * meta.itemsPerPage) + 1}
                        -{Math.min(meta.currentPage * meta.itemsPerPage, meta.totalItems)} of {formatNumber(meta.totalItems)}
                      </span>
                      <AdminTablePagination
                        currentPage={currentPage}
                        totalPages={meta.totalPages}
                        disabled={betsLoading}
                        onPageChange={loadBets}
                        nextDataCy="admin-user-bets-next-page"
                      />
                    </footer>
                  </section>
                </Col>
              </Row>
            ) : (
              <div className="admin-users-panel text-center text-muted">User not found</div>
            )}
          </>
        )}
      </Container>
    </div>
  );
};

export default UserDetail;
